import { forwardRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { Dialog, AppBar, Toolbar, IconButton, Typography, Box, Slide } from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import Iconify from 'src/components/Iconify';
import { DocusealForm } from '@docuseal/react';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  signingUrl: string | null;
  documentName: string;
  onClose: () => void;
  onComplete: () => void;
  onDeclineComplete?: () => void;
};

// ----------------------------------------------------------------------

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ----------------------------------------------------------------------

export default function DocuSealSignDialog({
  open,
  signingUrl,
  documentName,
  onClose,
  onComplete,
  onDeclineComplete,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const handleComplete = useCallback(() => {
    enqueueSnackbar('Document signed', { variant: 'success' });
    onComplete();
  }, [enqueueSnackbar, onComplete]);

  const handleDecline = useCallback(() => {
    enqueueSnackbar('Document signature declined', { variant: 'warning' });
    if (onDeclineComplete) {
      onDeclineComplete();
    } else {
      onClose();
    }
  }, [enqueueSnackbar, onClose, onDeclineComplete]);

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <Iconify icon="mdi:close" />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            Sign: {documentName}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {signingUrl && (
          <DocusealForm
            src={signingUrl}
            withTitle={false}
            withDecline
            withDownloadButton={false}
            withSendCopyButton={false}
            onComplete={handleComplete}
            onDecline={handleDecline}
          />
        )}
      </Box>
    </Dialog>
  );
}
